/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/lookupSourceApi.ts
 🗓️ Created: 2025-01-28
 🧩 Description:
    API functions for Caller ID Lookup Sources.
    Centralizes all API calls related to lookup sources.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  CallerIdLookupSource,
  ListLookupSourcesResponse,
  LookupSourcePayload,
} from '../types';

/**
 * Fetch Lookup Sources with pagination and search
 */
export async function fetchLookupSources(params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  domainId?: number;
}): Promise<ListLookupSourcesResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search && { search: params.search }),
    ...(params.domainId != null && { domainId: params.domainId.toString() }),
  });

  const url = `${buildApiUrl('/api/telephony/callerid-lookup-sources')}?${queryParams}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch Caller ID Lookup Sources');
  }

  return result;
}

/**
 * Fetch single Lookup Source by ID
 */
export async function fetchLookupSource(
  id: string | number,
): Promise<CallerIdLookupSource> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/callerid-lookup-sources/${id}`),
    {
      method: 'GET',
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch Caller ID Lookup Source');
  }

  return result.data;
}

/**
 * Create new Lookup Source
 */
export async function createLookupSource(
  payload: LookupSourcePayload,
): Promise<CallerIdLookupSource> {
  const response = await fetch(
    buildApiUrl('/api/telephony/callerid-lookup-sources'),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || 'Failed to create Caller ID Lookup Source');
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to create Caller ID Lookup Source');
  }

  return result.data;
}

/**
 * Update Lookup Source
 */
export async function updateLookupSource(
  id: string | number,
  payload: LookupSourcePayload,
): Promise<CallerIdLookupSource> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/callerid-lookup-sources/${id}`),
    {
      method: 'PUT',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || 'Failed to update Caller ID Lookup Source');
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to update Caller ID Lookup Source');
  }

  return result.data;
}

/**
 * Delete Lookup Source
 */
export async function deleteLookupSource(id: string | number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/callerid-lookup-sources/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    },
  );

  const result = await response.json().catch(() => null);

  if (!response.ok || !result?.success) {
    const message = result?.message || 'Failed to delete Caller ID Lookup Source';
    throw new Error(message);
  }
}

